import { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Popup,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../styles/TripsPage.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  "pk.eyJ1IjoibWFwbGFwYW5lIiwiYSI6ImNtaDN5aGwybDFnY3AwOHFwbzJrOTF4c2cifQ.sQB_UMSe27x9JRdllElxmw";

// Map icons
const tripStartIcon = new L.Icon({
  iconUrl: "/trip_start.png",
  iconSize: [26, 36],
  iconAnchor: [13, 36],
});

const tripEndIcon = new L.Icon({
  iconUrl: "/trip_end.png",
  iconSize: [26, 36],
  iconAnchor: [13, 36],
});

const alertIcon = new L.Icon({
  iconUrl: "/alert_pin.png",
  iconSize: [24, 30],
  iconAnchor: [12, 30],
});

export default function TripsPage() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // --- Load vehicles list ---
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const res = await fetch(`${API_URL}/v1/vehicles`);
        const data = await res.json();
        setVehicles(data);
      } catch (err) {
        console.error("Failed to fetch vehicles:", err);
      }
    };
    fetchVehicles();
  }, []);

  // --- Fetch trips ---
  const fetchTrips = async () => {
    if (!vehicleId) {
      setError("Please select a vehicle");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const params = new URLSearchParams({
        vehicleId,
        ...(dateRange.start && { start: dateRange.start }),
        ...(dateRange.end && { end: dateRange.end }),
      });
      const res = await fetch(`${API_URL}/v1/trips/report?${params.toString()}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setTrips(data.trips || []);
      setSelectedTrip(null);
    } catch (err) {
      console.error("Failed to fetch trips:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Download PDF ---
  const downloadPdf = () => {
    if (!vehicleId) return;
    const params = new URLSearchParams({
      vehicleId,
      ...(dateRange.start && { start: dateRange.start }),
      ...(dateRange.end && { end: dateRange.end }),
    });
    window.open(`${API_URL}/v1/trips/export.pdf?${params.toString()}`, "_blank");
  };

  return (
    <div className="trips-layout">
      {/* ---- Left Feed Panel ---- */}
      <div className="trips-feed">
        <div className="feed-header">
          <h2 className="feed-title">Vehicle Trips</h2>
        </div>

        {/* Filter Section */}
        <div className="filter-section">
          <select
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            className="filter-input"
          >
            <option value="">Select vehicle...</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.registrationNumber || v.registration || `Vehicle ${v.id}`}
              </option>
            ))}
          </select>

          <input
            type="datetime-local"
            value={dateRange.start}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, start: e.target.value }))
            }
            className="filter-input"
          />
          <input
            type="datetime-local"
            value={dateRange.end}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, end: e.target.value }))
            }
            className="filter-input"
          />

          <button onClick={fetchTrips} className="btn-fetch">
            {loading ? "Loading..." : "Fetch Trips"}
          </button>
        </div>

        {trips.length > 0 && (
          <button onClick={downloadPdf} className="btn-export">
            📄 Download PDF
          </button>
        )}

        {/* Trip List */}
        <div className="feed-list">
          {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          {trips.length === 0 && !loading && (
            <p className="text-gray-500 text-sm mt-3">No trips found.</p>
          )}
          {trips.map((trip, idx) => (
            <div
              key={idx}
              className={`trip-card ${selectedTrip === trip ? "selected" : ""
                }`}
              onClick={() => setSelectedTrip(trip)}
            >
              <div className="trip-header">
                <h3 className="trip-title">Trip {idx + 1}</h3>
                <span className="trip-duration">
                  {trip.durationHr?.toFixed(1)}h
                </span>
              </div>
              <div className="trip-body">
                <p>
                  <b>Start:</b> {new Date(trip.startTime).toLocaleTimeString()} <br />
                  <b>End:</b> {new Date(trip.endTime).toLocaleTimeString()} <br />
                  <b>Distance:</b> {trip.distanceKm?.toFixed(2)} km
                </p>
              </div>
              <div className="trip-footer">
                {new Date(trip.startTime).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---- Right Map Panel ---- */}
      <div className="trips-map">
        <MapContainer
          center={[-28.6, 24.657847]}
          zoom={6}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url={`https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
          />

          {selectedTrip && selectedTrip.path && (
            <>
              {/* Trip path (OSM LineString) */}
              <Polyline
                positions={selectedTrip.path.coordinates.map(
                  ([lon, lat]) => [lat, lon]
                )}
                color="#2563eb"
                weight={4}
              />

              {/* Trip start */}
              <Marker
                position={[selectedTrip.startLat, selectedTrip.startLon]}
                icon={tripStartIcon}
              >
                <Popup>Trip Start</Popup>
              </Marker>

              {/* Trip end */}
              <Marker
                position={[selectedTrip.endLat, selectedTrip.endLon]}
                icon={tripEndIcon}
              >
                <Popup>Trip End</Popup>
              </Marker>

              {/* Alerts during trip */}
              {(selectedTrip.alerts || []).map((a) => (
                <Marker key={a.id} position={[a.lat, a.lon]} icon={alertIcon}>
                  <Popup>
                    <b>{a.title}</b>
                    <br />
                    {a.severity}
                    <br />
                    {new Date(a.createdAt).toLocaleString()}
                  </Popup>
                </Marker>
              ))}
            </>
          )}

        </MapContainer>

        {/* Trip Summary Sidebar */}
        {selectedTrip && (
          <div className="trip-summary">
            <h3>Trip Summary</h3>
            <p>
              <b>Start:</b>{" "}
              {new Date(selectedTrip.startTime).toLocaleTimeString()} <br />
              <b>End:</b>{" "}
              {new Date(selectedTrip.endTime).toLocaleTimeString()} <br />
              <b>Duration:</b> {selectedTrip.durationHr?.toFixed(2)} h <br />
              <b>Distance:</b> {selectedTrip.distanceKm?.toFixed(2)} km <br />
              <b>Avg Speed:</b> {selectedTrip.avgSpeed?.toFixed(1)} km/h <br />
              <b>Fuel Used:</b> {selectedTrip.fuelUsed?.toFixed(2)} L <br />
              <b>Alerts:</b> {selectedTrip.alerts?.length ?? 0}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
